import { all, takeLatest, put, call, select, fork } from 'redux-saga/effects';
import get from 'lodash/get';
import {
    getPaymentMethods,
    addPayment,
    updatePayment,
    deletePayment,
    setDefaultPayment,
} from 'yoda-interfaces/lib/Account/AccountApi';
import Cookies from 'yoda-core-components/lib/helpers/Cookies/Cookies';
import FactorySaga from 'yoda-site-components/lib/sagas/FactorySaga';
import AnalyticsAction from 'yoda-site-components/lib/actions/AnalyticsAction';
import { PLCC_BANNER_REQUEST } from 'yoda-site-components/lib/actionTypes/PLCCBannerActionTypes';
import ClientSideLoggerAction from 'yoda-site-components/lib/actions/ClientLoggerAction';
import { selectPlccBannerThrottleFF } from 'yoda-site-components/lib/selectors/plccBannerSelector';
import {
    getAccertifyDeviceInfo,
    logExtendedAnalytics,
} from 'yoda-site-components/lib/sagas/SiteProbingSaga';
import * as AddressActionTypes from '../actionTypes/AddressActionTypes';
import { walletCaptchaAnalytics } from '../actions/WalletBarcodeActions';
import * as PaymentMethodsActionTypes from '../actionTypes/PaymentMethodsActionTypes';

const context = (state) => state.context;

export function* getPaymentMethodsSaga(action) {
    try {
        const response = yield call(FactorySaga, getPaymentMethods, action);
        const apiResponse = get(response, 'response.data', {});
        const payload = {
            isLoading: false,
            isSuccess: response.isSuccess,
        };

        if (response.isSuccess) {
            payload.paymentMethods = [];
            payload.defaultPaymentMethod = {};
            const isPaymentMethodAvailable = get(apiResponse, 'data.length');

            if (isPaymentMethodAvailable) {
                payload.paymentMethods = apiResponse.data;
                for (let i = 0; i < payload.paymentMethods.length; i += 1) {
                    if (payload.paymentMethods[i].isDefault) {
                        payload.defaultPaymentMethod = payload.paymentMethods[i];
                    }
                }
            }
            yield all([
                put({ type: PaymentMethodsActionTypes.GET_PAYMENT_METHODS_SUCCESS, payload }),
            ]);
        } else {
            payload.error = apiResponse;
            yield put({
                type: PaymentMethodsActionTypes.GET_PAYMENT_METHODS_ERROR,
                payload,
            });
        }
    } catch (error) {
        yield put({
            type: PaymentMethodsActionTypes.GET_PAYMENT_METHODS_ERROR,
            payload: {
                isSuccess: false,
                error,
            },
        });
    }
}

export function* getPlccBannerDetails(requestFrom) {
    const enablePlccBanner = yield select(selectPlccBannerThrottleFF);
    if (enablePlccBanner) {
        yield put({ type: PLCC_BANNER_REQUEST, requestFrom });
    }
}

function* triggerAnalyticsZeroAuth(analyticsParam) {
    const { payload, actionName, response, quantumSessionID, traceID } = analyticsParam;
    const errorData = {
        error: { errorDescription: payload.error?.errorMessage || '' },
        fraudForceErrData: { src: '4' },
    };
    yield put(AnalyticsAction.triggerFormError(errorData));
    yield put(
        ClientSideLoggerAction.logError(
            'Error in $0 auth',
            `actionName: | CAM_${actionName}_credit_Card$auth | responseCode: ${response.response.status} | user-agent:${window?.navigator?.userAgent} | quantumSessionID:${quantumSessionID} | traceID:${traceID}`
        )
    );
}

export function* updatePaymentMethodSaga(action) {
    try {
        const appContext = yield select(context);
        const { enableEnterpriseV3Captcha, enableAccertifyADI } =
            (appContext || {}).featureFlags || {};
        const modifyAction = { ...action };
        const deviceInfo = yield call(getAccertifyDeviceInfo, {
            actionName: 'CAM_update_credit_card',
        });
        if (deviceInfo) {
            modifyAction.payload = modifyAction.payload || {};
            modifyAction.payload.deviceInfo = deviceInfo;
        }
        const response = yield call(FactorySaga, updatePayment, modifyAction);
        const headers = get(response, 'response.headers');
        const captchaHeader = headers && headers.get('x-svrc');
        const traceID = headers && headers.get('x-trace-id');
        const quantumSessionID = Cookies.load('QuantumMetricSessionID') || '';
        const payload = {
            response: response.data || response.response.data,
        };
        const apiResponse = response.response;

        payload.isLoading = false;
        payload.requestFrom = 'UPDATE';
        if (apiResponse.status === 204 || apiResponse.status === 201) {
            payload.isSuccess = true;
            payload.isWalletOperationSuccess = true;
            if (enableEnterpriseV3Captcha) {
                yield put(walletCaptchaAnalytics('amuc', { code: captchaHeader }));
            }
            yield fork(getPlccBannerDetails, 'UPDATE_PAYMENT');
            if (action.payload.newAddress) {
                yield all([
                    put({ type: PaymentMethodsActionTypes.UPDATE_PAYMENT_METHOD_SUCCESS, payload }),
                    put({ type: AddressActionTypes.GET_ADDRESSES }),
                    put({ type: PaymentMethodsActionTypes.GET_PAYMENT_METHODS }),
                ]);
            } else {
                yield all([
                    put({ type: PaymentMethodsActionTypes.UPDATE_PAYMENT_METHOD_SUCCESS, payload }),
                    put({ type: PaymentMethodsActionTypes.GET_PAYMENT_METHODS }),
                ]);
            }
        } else {
            payload.isSuccess = false;
            payload.isWalletOperationSuccess = false;
            payload.error = response.data || response.response.data;
            if (enableEnterpriseV3Captcha && apiResponse.status === 400 && captchaHeader) {
                const errorData = {
                    error: { errorDescription: payload.error?.errorMessage || '' },
                    fraudForceErrData: { code: captchaHeader, src: '3' },
                };
                yield put(AnalyticsAction.triggerFormError(errorData));
                yield put(
                    ClientSideLoggerAction.logError(
                        'Error in processing captcha token',
                        `actionName: | CAM_update_credit_card |responseCode: ${apiResponse.status} | user-agent:${window?.navigator?.userAgent} | quantumSessionID:${quantumSessionID} | traceID:${traceID}`
                    )
                );
            } else if (
                response.response.status === 400 &&
                payload.error?.errorCode === 'KEY_OCS_CREDIT_CARD_AUTH_FAILED' &&
                !captchaHeader
            ) {
                const analyticsParam = {
                    payload,
                    actionName: 'update',
                    traceID,
                    quantumSessionID,
                    response,
                };
                yield call(triggerAnalyticsZeroAuth, analyticsParam);
            }
            yield all([
                put({
                    type: PaymentMethodsActionTypes.UPDATE_PAYMENT_METHOD_ERROR,
                    payload,
                }),
            ]);
        }
        /* Trigger ADI analytics only if we find accertify header as part of response */
        if (enableAccertifyADI) {
            const accertifyStatus = headers && headers.get('x-adi-sts');
            if (accertifyStatus && ['A', 'R'].includes(accertifyStatus)) {
                yield call(logExtendedAnalytics, {
                    actionMethod: 'CAM_UPDATE_CC',
                    result: response,
                });
            }
        }
    } catch (error) {
        yield all([
            put({
                type: PaymentMethodsActionTypes.UPDATE_PAYMENT_METHOD_ERROR,
                payload: {
                    isSuccess: false,
                    isWalletOperationSuccess: false,
                    error,
                },
            }),
            put(
                ClientSideLoggerAction.logError(
                    'update card in wallet failed due to JS error',
                    'Customer was not able to update the credit card'
                )
            ),
        ]);
    }
}

export function* addPaymentMethodSaga(action) {
    try {
        const appContext = yield select(context);
        const { enableEnterpriseV3Captcha, enableAccertifyADI } =
            (appContext || {}).featureFlags || {};
        const modifyAction = { ...action };
        const deviceInfo = yield call(getAccertifyDeviceInfo, {
            actionName: 'CAM_add_credit_card',
        });
        if (deviceInfo) {
            modifyAction.payload = modifyAction.payload || {};
            modifyAction.payload.deviceInfo = deviceInfo;
        }
        const response = yield call(FactorySaga, addPayment, modifyAction);
        const headers = get(response, 'response.headers');
        const captchaHeader = headers && headers.get('x-svrc');
        const traceID = headers && headers.get('x-trace-id');
        const quantumSessionID = Cookies.load('QuantumMetricSessionID') || '';
        const payload = {
            response: response.data || response.response.data,
        };
        payload.isLoading = false;
        payload.requestFrom = action.userAction ? `POST_${action.userAction}` : 'POST';
        if (response.response.status === 201) {
            payload.isSuccess = true;
            payload.isWalletOperationSuccess = true;
            yield fork(getPlccBannerDetails, 'ADD_PAYMENT');
            if (enableEnterpriseV3Captcha) {
                yield put(walletCaptchaAnalytics('amac', { code: captchaHeader }));
            }
            if (action.userAction === 'ADD') {
                yield all([
                    put({ type: PaymentMethodsActionTypes.ADD_PAYMENT_METHOD_SUCCESS, payload }),
                    put({ type: AddressActionTypes.GET_ADDRESSES }),
                    put({ type: PaymentMethodsActionTypes.GET_PAYMENT_METHODS }),
                ]);
            } else {
                yield all([
                    put({ type: PaymentMethodsActionTypes.ADD_PAYMENT_METHOD_SUCCESS, payload }),
                    put({ type: PaymentMethodsActionTypes.GET_PAYMENT_METHODS }),
                ]);
            }
        } else {
            payload.isSuccess = false;
            payload.isWalletOperationSuccess = false;
            payload.error = response.data || response.response.data;
            if (enableEnterpriseV3Captcha && response.response.status === 400 && captchaHeader) {
                const errorData = {
                    error: { errorDescription: payload.error?.errorMessage || '' },
                    fraudForceErrData: { code: captchaHeader, src: '3' },
                };
                yield put(AnalyticsAction.triggerFormError(errorData));
                yield put(
                    ClientSideLoggerAction.logError(
                        'Error in processing captcha token',
                        `actionName: | CAM_add_credit_card | responseCode: ${response.response.status} | user-agent:${window?.navigator?.userAgent} | quantumSessionID:${quantumSessionID} | traceID:${traceID}`
                    )
                );
            } else if (
                response.response.status === 400 &&
                payload.error?.errorCode === 'KEY_OCS_CREDIT_CARD_AUTH_FAILED' &&
                !captchaHeader
            ) {
                const analyticsParam = {
                    payload,
                    actionName: 'add',
                    traceID,
                    quantumSessionID,
                    response,
                };
                yield call(triggerAnalyticsZeroAuth, analyticsParam);
            }
            yield all([
                put({
                    type: PaymentMethodsActionTypes.ADD_PAYMENT_METHOD_ERROR,
                    payload,
                }),
            ]);
        }
        /*  Trigger ADI analytics only if we find accertify flag enabled and
            accertify header present as part of response */
        if (enableAccertifyADI) {
            const accertifyStatus = headers && headers.get('x-adi-sts');
            if (accertifyStatus && ['A', 'R'].includes(accertifyStatus)) {
                yield call(logExtendedAnalytics, {
                    actionMethod: 'CAM_ADD_CC',
                    result: response,
                });
            }
        }
    } catch (error) {
        yield all([
            put({
                type: PaymentMethodsActionTypes.ADD_PAYMENT_METHOD_ERROR,
                payload: {
                    isSuccess: false,
                    isWalletOperationSuccess: false,
                    error,
                },
            }),
            put(
                ClientSideLoggerAction.logError(
                    'add card to wallet failed due to JS error',
                    'Customer was not able to add the credit card to account'
                )
            ),
        ]);
    }
}

export function* deletePaymentMethodSaga(action) {
    try {
        const response = yield call(FactorySaga, deletePayment, action);
        const apiResponse = response.response;
        const payload = {
            paymentId: action.payload.id,
        };
        payload.requestFrom = 'DELETE';
        payload.isLoading = false;
        if (apiResponse.status < 400) {
            payload.isSuccess = true;
            payload.isWalletOperationSuccess = true;
            yield fork(getPlccBannerDetails, 'DELETE_PAYMENT');
            yield all([
                put({ type: PaymentMethodsActionTypes.DELETE_PAYMENT_METHOD_SUCCESS, payload }),
                put({ type: PaymentMethodsActionTypes.GET_PAYMENT_METHODS }),
            ]);
        } else {
            payload.isSuccess = false;
            payload.isWalletOperationSuccess = false;
            payload.error = response.data || response.response.data;
            yield put({
                type: PaymentMethodsActionTypes.DELETE_PAYMENT_METHOD_ERROR,
                payload,
            });
        }
    } catch (error) {
        yield put({
            type: PaymentMethodsActionTypes.DELETE_PAYMENT_METHOD_ERROR,
            payload: {
                isSuccess: false,
                isWalletOperationSuccess: false,
                error,
            },
        });
    }
}

export function* setDefaultPaymentMethod(action) {
    try {
        const response = yield call(FactorySaga, setDefaultPayment, action);
        const payload = {
            response: { id: action.payload.id, expressCheckout: true, isDefault: true },
            requestFrom: 'PATCH_FOR_DEFAULT_CARD',
            isLoading: false,
            isSuccess: true,
            isWalletOperationSuccess: true,
        };
        if (response.response.status === 204) {
            yield fork(getPlccBannerDetails, 'DEFAULT_PAYMENT');
            yield all([
                put({ type: PaymentMethodsActionTypes.UPDATE_PAYMENT_METHOD_SUCCESS, payload }),
            ]);
        } else {
            payload.isSuccess = false;
            payload.isWalletOperationSuccess = false;
            payload.error = response.data || response.response.data;
            yield put({
                type: PaymentMethodsActionTypes.UPDATE_PAYMENT_METHOD_ERROR,
                payload,
            });
        }
    } catch (error) {
        yield put({
            type: PaymentMethodsActionTypes.UPDATE_PAYMENT_METHOD_ERROR,
            payload: {
                isSuccess: false,
                isWalletOperationSuccess: false,
                error,
            },
        });
    }
}

function* watchPaymentMethodSaga() {
    yield takeLatest(PaymentMethodsActionTypes.GET_PAYMENT_METHODS, getPaymentMethodsSaga);
    yield takeLatest(PaymentMethodsActionTypes.UPDATE_PAYMENT_METHOD, updatePaymentMethodSaga);
    yield takeLatest(PaymentMethodsActionTypes.ADD_PAYMENT_METHOD, addPaymentMethodSaga);
    yield takeLatest(PaymentMethodsActionTypes.DELETE_PAYMENT_METHOD, deletePaymentMethodSaga);
    yield takeLatest(PaymentMethodsActionTypes.SET_DEFAULT_PAYMENT_METHOD, setDefaultPaymentMethod);
}

watchPaymentMethodSaga.sagaName = 'paymentMethodSaga';
export default watchPaymentMethodSaga;
